import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const EnvelopeAnimation = ({ children, onAnimationComplete }) => {
  const [showEnvelope, setShowEnvelope] = useState(true);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    // Start the animation sequence
    const timer1 = setTimeout(() => {
      setShowEnvelope(false);
    }, 2000); // Show envelope for 2 seconds

    const timer2 = setTimeout(() => {
      setShowContent(true);
      if (onAnimationComplete) {
        onAnimationComplete();
      }
    }, 3000); // Show content after 3 seconds

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [onAnimationComplete]);

  return (
    <div className={`envelope-container ${showContent ? 'showing-content' : ''}`}>
      <AnimatePresence>
        {showEnvelope && (
          <motion.div
            className="envelope-wrapper"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ 
              opacity: 0, 
              scale: 0.8,
              rotateY: 180,
              transition: { duration: 1, ease: "easeInOut" }
            }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="envelope">
              <div className="envelope-back"></div>
              <div className="envelope-front">
                <div className="envelope-flap"></div>
                <div className="envelope-seal">
                  <div className="seal-heart"><i className="fas fa-heart"></i></div>
                </div>
              </div>
              <div className="envelope-content">
                <div className="envelope-paper">
                  <div className="paper-content">
                    <h3>You're Invited!</h3>
                    <p>Cami & Eze's Wedding</p>
                    <p>March 28, 2026</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showContent && (
          <motion.div
            className="form-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx>{`
        .envelope-container {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: linear-gradient(135deg, #5D7A8C 0%, #8BA6B8 50%, #A8C4D9 100%);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow-y: auto;
          overflow-x: hidden;
        }

        .envelope-container.showing-content {
          align-items: flex-start;
        }

        .envelope-wrapper {
          perspective: 1000px;
        }

        .envelope {
          width: 80vw;
          height: 60vh;
          max-width: 800px;
          max-height: 600px;
          position: relative;
          transform-style: preserve-3d;
        }

        .envelope-back {
          position: absolute;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border: 2px solid #dee2e6;
          border-radius: 8px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }

        .envelope-front {
          position: absolute;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
          border: 2px solid #dee2e6;
          border-radius: 8px;
          transform-origin: bottom;
          animation: envelopeOpen 2s ease-in-out 1s forwards;
        }

        .envelope-flap {
          position: absolute;
          top: -2px;
          left: -2px;
          right: -2px;
          height: 50%;
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
          border: 2px solid #dee2e6;
          border-bottom: none;
          border-radius: 8px 8px 0 0;
          transform-origin: bottom;
          animation: flapOpen 2s ease-in-out 1s forwards;
        }

        .envelope-seal {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 120px;
          height: 120px;
          background: linear-gradient(135deg, #5D7A8C 0%, #8BA6B8 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(93, 122, 140, 0.3);
          animation: sealBreak 1.5s ease-in-out 1.5s forwards;
        }

        .seal-heart {
          font-size: 60px;
          color: #ffffff;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
          animation: heartBeat 1.2s ease-in-out infinite alternate;
          transition: all 0.3s ease;
        }

        .envelope-content {
          position: absolute;
          top: 10px;
          left: 10px;
          right: 10px;
          bottom: 10px;
          opacity: 0;
          animation: contentReveal 1s ease-in-out 2.5s forwards;
        }

        .envelope-paper {
          width: 100%;
          height: 100%;
          background: #ffffff;
          border-radius: 4px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .paper-content {
          text-align: center;
          color: #333;
        }

        .paper-content h3 {
          font-size: 3rem;
          margin-bottom: 20px;
          color: #ff6b6b;
          font-weight: 600;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .paper-content p {
          margin: 10px 0;
          color: #666;
          font-size: 1.5rem;
          font-weight: 500;
        }

        .form-content {
          position: relative;
          z-index: 1;
          background: transparent;
          min-height: 100vh;
          width: 100%;
          overflow: visible;
        }

        @keyframes envelopeOpen {
          0% { transform: rotateX(0deg); }
          100% { transform: rotateX(-180deg); }
        }

        @keyframes flapOpen {
          0% { transform: rotateX(0deg); }
          100% { transform: rotateX(-90deg); }
        }

        @keyframes sealBreak {
          0% { 
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
          50% { 
            transform: translate(-50%, -50%) scale(1.2);
            opacity: 0.8;
          }
          100% { 
            transform: translate(-50%, -50%) scale(0);
            opacity: 0;
          }
        }

        @keyframes heartBeat {
          0% { transform: scale(1); }
          100% { transform: scale(1.1); }
        }

        @keyframes contentReveal {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        @media (max-width: 768px) {
          .envelope {
            width: 90vw;
            height: 50vh;
            max-width: none;
            max-height: none;
          }
          
          .paper-content h3 {
            font-size: 2rem;
          }
          
          .paper-content p {
            font-size: 1.2rem;
          }
        }
      `}</style>
    </div>
  );
};

export default EnvelopeAnimation;
